/**
 * Points Service
 * 
 * Manages points calculation, rewards logic, and point-to-reward conversions.
 * This service is backend-agnostic and can work with any database.
 * 
 * FOR NEXT DEVELOPER:
 * 1. Replace all Firebase calls with your actual database calls
 * 2. Update POINTS_MULTIPLIER if company changes rewards rate
 * 3. Modify REWARD_TIERS if adding new rewards
 */

import { db } from "../config/firebase"
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
} from "firebase/firestore"

// ==================== CONFIGURATION ====================

/**
 * Points multiplier: $1 spent = X points
 * Adjust this if the rewards rate changes
 */
export const POINTS_MULTIPLIER = 1 // $1 = 1 point

/**
 * Reward tiers - what users get at each point level
 * CUSTOMIZE THIS based on your business logic
 */
export const REWARD_TIERS = {
  10000: {
    name: "Luxury Package Experience",
    description: "1 Free Hotel Stay + 1 Wellness Session + 1 PB Pavilion Experience",
    value: "~$450+",
    components: {
      hotelStay: true,
      wellnessSession: true,
      pavilionExperience: true,
    },
  },
  // Add more tiers as needed
  // 25000: { name: "Premium Package", ... }
  // 50000: { name: "Elite Package", ... }
}

// ==================== POINTS CALCULATION ====================

/**
 * Calculate points earned from a purchase
 * 
 * @param {number} amount - Purchase amount in USD
 * @param {string} itemType - 'suite' | 'wellness' | 'pavilion'
 * @returns {number} Points earned
 */
export const calculatePointsFromPurchase = (amount, itemType = null) => {
  // Base calculation: $1 = POINTS_MULTIPLIER points
  let points = Math.floor(amount * POINTS_MULTIPLIER)

  // OPTIONAL: Add bonus multipliers for specific item types
  // Example: Wellness purchases earn 1.5x points
  // if (itemType === "wellness") {
  //   points = Math.floor(points * 1.5)
  // }

  return points
}

/**
 * Calculate cost to redeem a reward
 * 
 * @param {number} pointsNeeded - Points required for reward
 * @returns {number} Dollar value of the reward
 */
export const calculateRewardValue = (pointsNeeded) => {
  // Inverse of points calculation: pointsNeeded / POINTS_MULTIPLIER = USD value
  return pointsNeeded / POINTS_MULTIPLIER
}

// ==================== USER POINTS MANAGEMENT ====================

/**
 * Get user's current points from Firebase
 * NEXT DEV: Replace with your database query
 * 
 * @param {string} userId - Firebase user ID
 * @returns {Promise<Object>} User points data
 */
export const getUserPoints = async (userId) => {
  try {
    const userPointsRef = doc(db, "users", userId, "rewards", "points")
    const docSnap = await getDoc(userPointsRef)

    if (docSnap.exists()) {
      return docSnap.data() // { currentPoints: 250, totalEarned: 500, rewardsRedeemed: 250 }
    } else {
      // First time user - initialize with 0 points
      await initializeUserPoints(userId)
      return {
        currentPoints: 0,
        totalEarned: 0,
        rewardsRedeemed: 0,
        lastUpdated: new Date(),
      }
    }
  } catch (error) {
    console.error("Error fetching user points:", error)
    throw error
  }
}

/**
 * Initialize points for new user
 * NEXT DEV: Replace with your database insert
 * 
 * @param {string} userId - Firebase user ID
 */
export const initializeUserPoints = async (userId) => {
  try {
    const userPointsRef = doc(db, "users", userId, "rewards", "points")
    await setDoc(userPointsRef, {
      currentPoints: 0,
      totalEarned: 0,
      rewardsRedeemed: 0,
      lastUpdated: serverTimestamp(),
    })
    console.log("Initialized points for user:", userId)
  } catch (error) {
    console.error("Error initializing user points:", error)
    throw error
  }
}

/**
 * Add points to user's account (called after payment capture)
 * NEXT DEV: Replace with your database update
 * 
 * @param {string} userId - Firebase user ID
 * @param {number} pointsToAdd - Points to add
 * @param {string} source - Why points were added (e.g., "suite_booking", "wellness_session")
 * @returns {Promise<Object>} Updated points data
 */
export const addPointsToUser = async (userId, pointsToAdd, source = "purchase") => {
  try {
    const userPointsRef = doc(db, "users", userId, "rewards", "points")

    // Get current points first
    const currentData = await getUserPoints(userId)

    // Calculate new totals
    const newCurrentPoints = currentData.currentPoints + pointsToAdd
    const newTotalEarned = currentData.totalEarned + pointsToAdd

    // Update user's points
    await updateDoc(userPointsRef, {
      currentPoints: newCurrentPoints,
      totalEarned: newTotalEarned,
      lastUpdated: serverTimestamp(),
    })

    // Log transaction in points history
    await logPointsTransaction(userId, {
      type: "earned",
      points: pointsToAdd,
      source,
      newBalance: newCurrentPoints,
    })

    console.log(`Added ${pointsToAdd} points to user ${userId}`)

    return {
      currentPoints: newCurrentPoints,
      totalEarned: newTotalEarned,
      rewardsRedeemed: currentData.rewardsRedeemed,
      pointsAdded: pointsToAdd,
    }
  } catch (error) {
    console.error("Error adding points:", error)
    throw error
  }
}

/**
 * Redeem points for a reward
 * NEXT DEV: Replace with your database transaction
 * 
 * @param {string} userId - Firebase user ID
 * @param {number} pointsToRedeem - Points to redeem
 * @param {string} rewardName - Name of reward being redeemed
 * @returns {Promise<Object>} Updated points and reward confirmation
 */
export const redeemPointsForReward = async (userId, pointsToRedeem, rewardName) => {
  try {
    const userPointsRef = doc(db, "users", userId, "rewards", "points")
    const currentData = await getUserPoints(userId)

    // Check if user has enough points
    if (currentData.currentPoints < pointsToRedeem) {
      throw new Error(
        `Insufficient points. Have ${currentData.currentPoints}, need ${pointsToRedeem}`
      )
    }

    // Deduct points
    const newCurrentPoints = currentData.currentPoints - pointsToRedeem
    const newRewardsRedeemed = currentData.rewardsRedeemed + pointsToRedeem

    await updateDoc(userPointsRef, {
      currentPoints: newCurrentPoints,
      rewardsRedeemed: newRewardsRedeemed,
      lastUpdated: serverTimestamp(),
    })

    // Log transaction
    await logPointsTransaction(userId, {
      type: "redeemed",
      points: pointsToRedeem,
      source: rewardName,
      newBalance: newCurrentPoints,
    })

    console.log(`Redeemed ${pointsToRedeem} points for ${rewardName}`)

    return {
      success: true,
      currentPoints: newCurrentPoints,
      pointsRedeemed: pointsToRedeem,
      rewardName,
    }
  } catch (error) {
    console.error(" Error redeeming points:", error)
    throw error
  }
}

// ==================== POINTS HISTORY ====================

/**
 * Log a points transaction (earned or redeemed)
 * NEXT DEV: Replace with your database insert
 * 
 * @param {string} userId - Firebase user ID
 * @param {Object} transaction - Transaction details
 */
export const logPointsTransaction = async (userId, transaction) => {
  try {
    const transactionsRef = collection(db, "users", userId, "pointsHistory")
    await setDoc(doc(transactionsRef), {
      ...transaction,
      timestamp: serverTimestamp(),
    })
  } catch (error) {
    console.error("Error logging transaction:", error)
    // Don't throw - transaction was successful even if logging failed
  }
}

/**
 * Get user's points transaction history
 * NEXT DEV: Replace with your database query
 * 
 * @param {string} userId - Firebase user ID
 * @returns {Promise<Array>} List of transactions
 */
export const getPointsHistory = async (userId) => {
  try {
    const transactionsRef = collection(db, "users", userId, "pointsHistory")
    const q = query(transactionsRef)
    const querySnapshot = await getDocs(q)

    const transactions = []
    querySnapshot.forEach((doc) => {
      transactions.push({
        id: doc.id,
        ...doc.data(),
      })
    })

    // Sort by timestamp descending (newest first)
    return transactions.sort((a, b) => b.timestamp - a.timestamp)
  } catch (error) {
    console.error("Error fetching points history:", error)
    return []
  }
}

export default {
  calculatePointsFromPurchase,
  calculateRewardValue,
  getUserPoints,
  initializeUserPoints,
  addPointsToUser,
  redeemPointsForReward,
  logPointsTransaction,
  getPointsHistory,
}